/**
 * @file sheets/side-sheet/side-sheet.tsx
 *
 * Standard (non-modal) Side Sheet following MD3 specification.
 *
 * - Slides from `start` (left in LTR) or `end` (right in LTR) edge
 * - Coplanar sibling support via `coplanarSiblingRef` — animates adjacent
 *   content's margin using a CSS transition
 * - Inline rendering (no portal) — does not block background interaction
 * - WAI-ARIA: `role="complementary"`
 *
 * @see https://m3.material.io/components/side-sheets/overview
 */

import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils";
import { ScrollArea } from "../../scroll-area";
import { getMotionTransitionCSS } from "../../shared/motion-tokens";
import type { BaseSheetProps, SheetEdge } from "../shared/types";
import { buildSideSheetAnim, resolveDividers } from "../shared/utils";

const COPLANAR_TRANSITION = `margin ${getMotionTransitionCSS("default", "spatial")}`;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the standard (non-modal) Side Sheet.
 */
export interface SideSheetProps extends BaseSheetProps {
	/**
	 * Which edge the sheet slides in from.
	 * - `start` — left in LTR, right in RTL
	 * - `end`   — right in LTR, left in RTL
	 * @default "end"
	 */
	edge?: SheetEdge;

	/**
	 * Width of the sheet.
	 * @default "360px"
	 */
	width?: string;

	/**
	 * Ref to the sibling element that should animate its margin when the
	 * sheet opens/closes (coplanar layout).
	 *
	 * @example
	 * ```tsx
	 * const mainRef = useRef<HTMLDivElement>(null);
	 * <main ref={mainRef}>...</main>
	 * <SideSheet coplanarSiblingRef={mainRef} isOpen={open} onClose={handleClose}>
	 *   ...
	 * </SideSheet>
	 * ```
	 */
	coplanarSiblingRef?: React.RefObject<HTMLElement | null>;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Standard (non-modal) Side Sheet that slides in from a screen edge.
 *
 * Does NOT block background interaction. Pass `coplanarSiblingRef` to
 * enable automatic margin animation on an adjacent element.
 */
export const SideSheet = React.forwardRef<HTMLElement, SideSheetProps>(
	(
		{
			isOpen,
			children,
			className,
			edge = "end",
			width = "360px",
			coplanarSiblingRef,
			portal = true,
			"aria-labelledby": ariaLabelledBy,
			"aria-label": ariaLabel,
			header,
			footer,
			divider,
			scrollAreaProps,
		},
		ref,
	) => {
		const sheetAnim = React.useMemo(() => buildSideSheetAnim(edge), [edge]);

		const [mounted, setMounted] = React.useState(false);
		React.useEffect(() => {
			setMounted(true);
		}, []);

		const resolvedDividers = React.useMemo(
			() => resolveDividers(divider),
			[divider],
		);

		// Coplanar sibling margin animation
		React.useEffect(() => {
			const sibling = coplanarSiblingRef?.current;
			if (!sibling) return;

			sibling.style.transition = COPLANAR_TRANSITION;
			const marginProp = edge === "end" ? "marginRight" : "marginLeft";
			sibling.style[marginProp] = isOpen ? width : "";

			return () => {
				sibling.style.marginRight = "";
				sibling.style.marginLeft = "";
				sibling.style.transition = "";
			};
		}, [isOpen, edge, width, coplanarSiblingRef]);

		const content = (
			<AnimatePresence>
				{isOpen && (
					<m.aside
						ref={ref as React.Ref<HTMLElement>}
						role="complementary"
						aria-label={ariaLabel}
						aria-labelledby={ariaLabelledBy}
						tabIndex={-1}
						className={cn(
							"fixed top-0 bottom-0 z-40",
							edge === "end" ? "right-0" : "left-0",
							"bg-m3-surface-container-low",
							"flex flex-col overflow-hidden",
							edge === "end"
								? "border-l border-m3-outline-variant"
								: "border-r border-m3-outline-variant",
							"outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-inset",
							className,
						)}
						style={{ width }}
						{...sheetAnim}
					>
						{header && <div className="shrink-0 px-6 py-4">{header}</div>}
						{header && resolvedDividers.header && (
							<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
						)}

						<ScrollArea
							type="hover"
							{...scrollAreaProps}
							className={cn(
								"flex-1 min-h-0 min-w-0",
								scrollAreaProps?.className,
							)}
							viewportClassName={cn(
								"overscroll-contain",
								scrollAreaProps?.viewportClassName,
							)}
							viewportProps={scrollAreaProps?.viewportProps}
						>
							{children}
						</ScrollArea>

						{footer && resolvedDividers.footer && (
							<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
						)}
						{footer && <div className="shrink-0 px-6 py-4">{footer}</div>}
					</m.aside>
				)}
			</AnimatePresence>
		);

		if (portal) {
			if (!mounted) return null;
			return createPortal(content, document.body);
		}

		return content;
	},
);

SideSheet.displayName = "SideSheet";
